import { memo, useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { HeatmapGrid } from '@4cm/core/tactics/overlays';

/**
 * 3D-native heatmap overlay. Replaces the SVG `HeatmapLayer2D` for
 * the 3D board so the density grid sits flat on the pitch and gets
 * proper perspective projection from the R3F camera.
 *
 * Render strategy: one `InstancedMesh` of `step × step` plane instances
 * lying on the pitch (y ≈ 0.006). Each surviving cell (intensity ≥
 * `INTENSITY_CULL_THRESHOLD`) gets one instance, positioned at the
 * cell's world centre with a per-instance colour from the cool→hot
 * ramp.
 *
 * The 2D version achieved fade-out at the cool end via per-cell alpha
 * (`alpha = intensity * opacity`). Standard `InstancedMesh.instanceColor`
 * only carries RGB, not alpha, so we approximate the effect by
 * **multiplying the ramp colour by intensity² before storing it** and
 * compositing with additive blending — low-intensity cells contribute
 * very little brightness over the dark grass; high-intensity cells
 * glow. The material's master opacity multiplies on top.
 */

/** Skip cells below this normalised intensity — matches the 2D layer. */
export const INTENSITY_CULL_THRESHOLD = 0.05;

/** 4-stop cool→hot ramp matching `HeatmapLayer2D`. */
interface RampStop {
  t: number;
  rgb: [number, number, number];
}
const RAMP: RampStop[] = [
  { t: 0.0, rgb: [56, 130, 232] },   // cool blue
  { t: 0.4, rgb: [74, 222, 128] },   // green
  { t: 0.7, rgb: [251, 191, 36] },   // yellow / amber
  { t: 1.0, rgb: [239, 68, 68] },    // red
];

function rampColor(t: number): [number, number, number] {
  if (!(t > 0)) return RAMP[0]!.rgb;
  if (t >= 1) return RAMP[RAMP.length - 1]!.rgb;
  for (let i = 1; i < RAMP.length; i++) {
    const upper = RAMP[i]!;
    if (t <= upper.t) {
      const lower = RAMP[i - 1]!;
      const span = upper.t - lower.t;
      const f = span > 0 ? (t - lower.t) / span : 0;
      return [
        lower.rgb[0] + (upper.rgb[0] - lower.rgb[0]) * f,
        lower.rgb[1] + (upper.rgb[1] - lower.rgb[1]) * f,
        lower.rgb[2] + (upper.rgb[2] - lower.rgb[2]) * f,
      ];
    }
  }
  return RAMP[RAMP.length - 1]!.rgb;
}

export interface HeatmapLayer3DProps {
  grid: HeatmapGrid | null;
  /** Master opacity in [0, 1]. */
  opacity: number;
}

// Reusable scratch objects — avoid per-cell allocation in the effect.
const _dummy = new THREE.Object3D();
const _color = new THREE.Color();

function HeatmapLayer3DInner({ grid, opacity }: HeatmapLayer3DProps) {
  // Surviving cells + cell step. Keyed only on the grid so the
  // InstancedMesh isn't reconstructed for unrelated parent renders.
  const cells = useMemo(() => {
    if (!grid) return null;
    const { nRows, nCols, step, halfL, halfW, intensity } = grid;
    const items: Array<{
      x: number;
      z: number;
      r: number;
      g: number;
      b: number;
    }> = [];
    for (let r = 0; r < nRows; r++) {
      const cx = -halfW + (r + 0.5) * step;
      for (let c = 0; c < nCols; c++) {
        const v = intensity[r * nCols + c]!;
        if (v < INTENSITY_CULL_THRESHOLD) continue;
        const cz = -halfL + (c + 0.5) * step;
        const [rr, gg, bb] = rampColor(v);
        // Pre-multiply colour by intensity² so dim cells barely glow
        // over the grass while peak cells blaze. The squared factor
        // mirrors the 2D layer's perceptual feel where intensity drove
        // alpha, not brightness.
        const k = v * v;
        items.push({
          x: cx,
          z: cz,
          r: (rr / 255) * k,
          g: (gg / 255) * k,
          b: (bb / 255) * k,
        });
      }
    }
    return { items, step };
  }, [grid]);

  // Stable geometry + material across the session — the InstancedMesh
  // is the only thing that re-mounts when the cell count changes.
  const geometry = useMemo(
    () => new THREE.PlaneGeometry(cells?.step ?? 1, cells?.step ?? 1),
    [cells?.step],
  );
  const material = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity,
        depthWrite: false,
        side: THREE.DoubleSide,
        // Additive against the dark grass — bright cells glow, dim
        // cells contribute almost nothing. Mirrors broadcast heatmap.
        blending: THREE.AdditiveBlending,
      }),
    [opacity],
  );

  const meshRef = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh || !cells) return;
    const { items } = cells;
    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      _dummy.position.set(item.x, 0, item.z);
      // Lie flat on the pitch — plane geometry defaults face +Z; rotate
      // -π/2 around X so its normal becomes +Y (pointing skyward).
      _dummy.rotation.set(-Math.PI / 2, 0, 0);
      _dummy.scale.set(1, 1, 1);
      _dummy.updateMatrix();
      mesh.setMatrixAt(i, _dummy.matrix);
      _color.setRGB(item.r, item.g, item.b);
      mesh.setColorAt(i, _color);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [cells]);

  if (!cells || cells.items.length === 0) return null;

  return (
    <instancedMesh
      ref={meshRef}
      // `args` is the InstancedMesh constructor signature:
      //   [geometry, material, count].
      args={[geometry, material, cells.items.length]}
      position={[0, 0.006, 0]}
      raycast={() => undefined}
      renderOrder={0}
    />
  );
}

export const HeatmapLayer3D = memo(HeatmapLayer3DInner);
